import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, ChefHat, Clock, Users, Filter, X, Star, History, Lock, Settings, Download, Upload, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { PasswordDialog } from "@/components/PasswordDialog";
import { ThemeToggle } from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_IMAGES = {
  "Breads": "/category-fallbacks/breads.jpg",
  "Soups": "/category-fallbacks/soups.jpg",
  "Main Dishes": "/category-fallbacks/main-dishes.jpg",
  "Sides": "/category-fallbacks/sides.jpg",
  "Preserves": "/category-fallbacks/preserves.jpg",
  "Desserts": "/category-fallbacks/desserts.jpg"
};

const DEFAULT_RECIPE_IMAGE = "/category-fallbacks/default.jpg";

// Helper to manage recently viewed in localStorage
const getRecentlyViewed = () => {
  try {
    const stored = localStorage.getItem('recentlyViewedRecipes');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const getRecipeImage = (recipe) => {
  if (recipe.image_data) return recipe.image_data;
  if (recipe.image_url) return recipe.image_url;
  return CATEGORY_IMAGES[recipe.category] || DEFAULT_RECIPE_IMAGE;
};

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders, password, logout } = useAuth();
  const restoreInputRef = useRef(null);

  const handleBackup = async () => {
    try {
      const res = await axios.get(`${API}/recipes`);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `burnsy-bistro-backup-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${res.data.length} recipes)`);
    } catch (e) {
      toast.error("Backup failed");
    }
  };

  const handleRestoreClick = () => {
    if (!isAuthenticated) {
      toast.error("Authentication required");
      return;
    }
    restoreInputRef.current?.click();
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting same file fires onChange
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        toast.error("Invalid backup: expected an array of recipes");
        return;
      }
      const res = await axios.post(
        `${API}/admin/import`,
        { recipes: parsed },
        { headers: getAuthHeaders() }
      );
      const { upserted, modified, matched, skipped } = res.data;
      toast.success(
        `Restore complete: ${upserted} new, ${modified} updated, ${matched - modified} unchanged${skipped ? `, ${skipped} skipped` : ""}`
      );
      // Refresh recipes list
      window.location.reload();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Restore failed";
      toast.error(`Restore failed: ${msg}`);
    }
  };
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTag, setSelectedTag] = useState(searchParams.get("tag") || null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFavorites, setShowFavorites] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  const handleAddRecipe = () => {
    if (isAuthenticated) {
      navigate('/add');
    } else {
      setPasswordDialogOpen(true);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchTags();
    fetchRecipes();
    setRecentlyViewed(getRecentlyViewed());
  }, []);

  useEffect(() => {
    fetchRecipes();
    fetchTags();
    // re-fetch on auth change so the gated /api/recipes call retries automatically
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedTag, searchQuery, showFavorites, password]);

  // Sync selectedTag → URL ?tag= so deep links work
  useEffect(() => {
    if (selectedTag) {
      setSearchParams({ tag: selectedTag }, { replace: true });
    } else if (searchParams.get("tag")) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTag]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedTag) params.append("tag", selectedTag);
      if (searchQuery) params.append("search", searchQuery);

      const response = await axios.get(`${API}/recipes?${params.toString()}`);
      let fetchedRecipes = response.data;
      
      // Filter favorites if needed
      if (showFavorites) {
        fetchedRecipes = fetchedRecipes.filter(r => r.is_favorite);
      }
      
      setRecipes(fetchedRecipes);
    } catch (error) {
      console.error("Error fetching recipes:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (e, recipeId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/recipes/${recipeId}/favorite`);
      setRecipes(prev => prev.map(r => 
        r.id === recipeId ? { ...r, is_favorite: response.data.is_favorite } : r
      ));
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedTag(null);
    setSearchQuery("");
    setShowFavorites(false);
  };

  // Get recently viewed recipes that exist in our recipes list
  const recentRecipes = recentlyViewed
    .map(id => recipes.find(r => r.id === id))
    .filter(Boolean)
    .slice(0, 4);

  const favoriteRecipes = recipes.filter(r => r.is_favorite);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/logo.png" 
              alt="Burnsy's Bistro" 
              className="h-14 w-auto object-contain"
            />
          </Link>
          
          <div className="flex items-center gap-2">
            <ThemeToggle />

            {/* Backup/restore are admin-only management actions — hide the whole
                menu until authenticated. Add Recipe stays visible as the login
                entry point. */}
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full"
                    data-testid="settings-menu-btn"
                    title="Settings"
                  >
                    <Settings className="w-5 h-5" strokeWidth={1.5} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleBackup}>
                    <Download className="w-4 h-4 mr-2" />
                    Download backup (JSON)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleRestoreClick}>
                    <Upload className="w-4 h-4 mr-2" />
                    Restore from backup
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      logout();
                      toast.success("Logged out");
                    }}
                    className="text-destructive focus:text-destructive"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <input
              ref={restoreInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleRestoreFile}
              className="hidden"
            />

            <Button
              className="rounded-full gap-2"
              data-testid="add-recipe-btn"
              onClick={handleAddRecipe}
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Add Recipe
              {!isAuthenticated && <Lock className="w-3 h-3 ml-1" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Password Dialog */}
      <PasswordDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        onSuccess={() => navigate('/add')}
      />

      {/* Hero Section */}
      <section className="px-6 py-12 md:py-16 max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground leading-tight">
            Burnsy's Personal
            <br />
            <span className="text-primary">Recipe Collection</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            All my favorite recipes in one place! This app makes it easy to prepare and cook for your family and friends!
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Search recipes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 h-12 rounded-full bg-input/50 border-transparent focus:border-primary"
              data-testid="search-input"
            />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="h-12 rounded-full gap-2 border-border"
                data-testid="filter-dropdown"
              >
                <Filter className="w-4 h-4" strokeWidth={1.5} />
                {selectedCategory || "All Categories"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => setSelectedCategory(null)}
                data-testid="filter-all"
              >
                All Categories
              </DropdownMenuItem>
              {categories.map((cat) => (
                <DropdownMenuItem 
                  key={cat} 
                  onClick={() => setSelectedCategory(cat)}
                  data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
                >
                  {cat}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button 
            variant={showFavorites ? "default" : "outline"}
            className="h-12 rounded-full gap-2"
            onClick={() => setShowFavorites(!showFavorites)}
            data-testid="favorites-filter-btn"
          >
            <Star className={`w-4 h-4 ${showFavorites ? "fill-current" : ""}`} strokeWidth={1.5} />
            Favorites
          </Button>

          {(selectedCategory || selectedTag || searchQuery || showFavorites) && (
            <Button
              variant="ghost"
              className="h-12 rounded-full gap-2"
              onClick={clearFilters}
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
              Clear
            </Button>
          )}
        </div>

        {/* Tag filter row */}
        {allTags.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">Tags:</span>
            {allTags.map((t) => {
              const active = selectedTag === t;
              return (
                <Badge
                  key={t}
                  variant={active ? "default" : "outline"}
                  className="rounded-full cursor-pointer hover:bg-secondary/80 transition-colors"
                  onClick={() => setSelectedTag(active ? null : t)}
                  data-testid={`filter-tag-${t}`}
                >
                  #{t}
                  {active && <X className="w-3 h-3 ml-1" strokeWidth={2} />}
                </Badge>
              );
            })}
          </div>
        )}
      </section>

      {/* Recently Viewed Section */}
      {!showFavorites && !selectedCategory && !selectedTag && !searchQuery && recentRecipes.length > 0 && (
        <section className="px-6 pb-8 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
            <h3 className="font-serif text-xl font-semibold text-foreground">Recently Viewed</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {recentRecipes.map((recipe) => (
              <Link 
                key={recipe.id} 
                to={`/recipe/${recipe.id}`}
                data-testid={`recent-recipe-${recipe.id}`}
              >
                <Card className="recipe-card overflow-hidden border-border/50 hover:shadow-md cursor-pointer h-full">
                  <div className="aspect-[4/3] overflow-hidden bg-muted">
                    <img
                      src={getRecipeImage(recipe)} loading="lazy" decoding="async"
                      alt={recipe.title}
                      className="recipe-card-image w-full h-full object-cover"
                      onError={(e) => { e.target.src = DEFAULT_RECIPE_IMAGE; }}
                    />
                  </div>
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm line-clamp-1">{recipe.title}</h4>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Favorites Quick Access */}
      {!showFavorites && !selectedCategory && !selectedTag && !searchQuery && favoriteRecipes.length > 0 && (
        <section className="px-6 pb-8 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-accent fill-accent" strokeWidth={1.5} />
              <h3 className="font-serif text-xl font-semibold text-foreground">Favorites</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="rounded-full"
              onClick={() => setShowFavorites(true)}
            >
              View all
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {favoriteRecipes.slice(0, 4).map((recipe) => (
              <Link 
                key={recipe.id} 
                to={`/recipe/${recipe.id}`}
                data-testid={`favorite-recipe-${recipe.id}`}
              >
                <Card className="recipe-card overflow-hidden border-border/50 hover:shadow-md cursor-pointer h-full relative">
                  <div className="absolute top-2 right-2 z-10">
                    <Star className="w-5 h-5 text-accent fill-accent" strokeWidth={1.5} />
                  </div>
                  <div className="aspect-[4/3] overflow-hidden bg-muted">
                    <img
                      src={getRecipeImage(recipe)} loading="lazy" decoding="async"
                      alt={recipe.title}
                      className="recipe-card-image w-full h-full object-cover"
                      onError={(e) => { e.target.src = DEFAULT_RECIPE_IMAGE; }}
                    />
                  </div>
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm line-clamp-1">{recipe.title}</h4>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recipe Grid */}
      <section className="px-6 pb-16 max-w-7xl mx-auto">
        <h3 className="font-serif text-xl font-semibold text-foreground mb-6">
          {showFavorites ? "Favorite Recipes" : selectedCategory ? selectedCategory : "All Recipes"}
        </h3>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <div className="aspect-[4/3] bg-muted skeleton" />
                <CardContent className="p-4">
                  <div className="h-6 bg-muted rounded skeleton mb-2" />
                  <div className="h-4 bg-muted rounded skeleton w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
              <ChefHat className="w-10 h-10 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-2xl font-semibold text-foreground mb-2">
              No recipes yet
            </h3>
            <p className="text-muted-foreground mb-6">
              {selectedCategory || selectedTag || searchQuery || showFavorites
                ? "No recipes match your search. Try different filters."
                : "Start building your collection by adding your first recipe."
              }
            </p>
            <Link to="/add">
              <Button className="rounded-full gap-2" data-testid="add-first-recipe-btn">
                <Plus className="w-4 h-4" strokeWidth={2} />
                Add Your First Recipe
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
            {recipes.map((recipe) => (
              <Link 
                key={recipe.id} 
                to={`/recipe/${recipe.id}`}
                data-testid={`recipe-card-${recipe.id}`}
              >
                <Card className="recipe-card overflow-hidden border-border/50 hover:shadow-lg cursor-pointer h-full relative group">
                  {/* Favorite Button */}
                  <button
                    onClick={(e) => toggleFavorite(e, recipe.id)}
                    className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
                    data-testid={`favorite-btn-${recipe.id}`}
                  >
                    <Star 
                      className={`w-4 h-4 ${recipe.is_favorite ? "text-accent fill-accent" : "text-muted-foreground"}`} 
                      strokeWidth={1.5} 
                    />
                  </button>
                  {recipe.is_favorite && (
                    <div className="absolute top-3 right-3 z-10 group-hover:opacity-0 transition-opacity">
                      <Star className="w-5 h-5 text-accent fill-accent" strokeWidth={1.5} />
                    </div>
                  )}
                  
                  <div className="aspect-[4/3] overflow-hidden bg-muted">
                    <img
                      src={getRecipeImage(recipe)} loading="lazy" decoding="async"
                      alt={recipe.title}
                      className="recipe-card-image w-full h-full object-cover"
                      onError={(e) => { e.target.src = DEFAULT_RECIPE_IMAGE; }}
                    />
                  </div>
                  <CardContent className="p-4">
                    <Badge 
                      variant="secondary" 
                      className="mb-2 text-xs font-medium"
                    >
                      {recipe.category}
                    </Badge>
                    <h3 className="font-serif text-lg font-semibold text-foreground line-clamp-2">
                      {recipe.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                      {recipe.cook_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {recipe.cook_time}
                        </span>
                      )}
                      {recipe.servings && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {recipe.servings}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Categories Section */}
      {!selectedCategory && !searchQuery && !showFavorites && recipes.length > 0 && (
        <section className="px-6 pb-16 max-w-7xl mx-auto">
          <h3 className="font-serif text-2xl font-semibold text-foreground mb-6">
            Browse by Category
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className="category-card group relative aspect-square rounded-2xl overflow-hidden"
                data-testid={`category-btn-${category.toLowerCase().replace(' ', '-')}`}
              >
                <img
                  src={CATEGORY_IMAGES[category]}
                  alt={category}
                  className="category-image w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white font-medium text-sm">
                  {category}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
